import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

// Markdown collapses single newlines into spaces; remark-breaks renders them as <br> so line breaks are preserved
const remarkPlugins = [remarkBreaks];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// HighlightedText only marks matches in a plain string; once rendered as markdown the body is an
// element tree, so this walks the hast tree and marks every text node using the same matching.
// Known limitation: search filters the raw markdown source, so a query matching syntax that markdown
// consumes (e.g. "*" turned into emphasis) can match a message without a visible highlight here,
// since that character no longer exists in the rendered text.
const rehypeHighlight = (query) => (tree) => {
  if (!query) {
    return;
  }
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  const q = query.toLowerCase();
  const walk = (node) => {
    if (!node.children) {
      return;
    }
    const out = [];
    for (const child of node.children) {
      if (child.type === 'text' && regex.test(child.value)) {
        regex.lastIndex = 0;
        for (const part of child.value.split(regex)) {
          if (part === '') {
            continue;
          }
          out.push(
            part.toLowerCase() === q
              ? { type: 'element', tagName: 'mark', properties: {}, children: [{ type: 'text', value: part }] }
              : { type: 'text', value: part }
          );
        }
      } else {
        if (child.tagName !== 'mark') {
          walk(child);
        }
        out.push(child);
      }
    }
    node.children = out;
  };
  walk(tree);
};

const components = {
  a: ({ href, children }) => (
    <a href={href} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  )
};

const MarkdownMessage = React.memo(({ text, searchQuery, sx }) => {
  const rehypePlugins = useMemo(() => (searchQuery ? [[rehypeHighlight, searchQuery]] : []), [searchQuery]);
  return (
    <Box
      sx={{
        fontFamily: (theme) => theme.typography.fontFamily,
        fontSize: '14px',
        overflowWrap: 'anywhere',
        '& p': { m: 0 },
        '& p + p': { mt: 1 },
        '& mark': { backgroundColor: '#fff176', borderRadius: '2px' },
        ...sx
      }}
    >
      <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
        {text || ''}
      </ReactMarkdown>
    </Box>
  );
});
MarkdownMessage.displayName = 'MarkdownMessage';

export default MarkdownMessage;
